import type { ActionType } from "typesafe-actions";
import { isActionOf } from "typesafe-actions";

import {
    getDefaultContactsCrmValue,
    getUniqueFieldName,
} from "app/pages/sales-intelligence/common-components/export-table/utils";
import type {
    AccountsFieldKey,
    SimilarwebFieldsConfig,
} from "app/pages/sales-intelligence/common-components/export-table/types";

import type { Integration } from "../types";
import {
    getConfigByExportName,
    getExportSettingSourceType,
    getExportSettingTitle,
    isFieldEligibleForEnrichment,
} from "../utils/utils";

import { sendParsedAsync } from "./actions/suppressionListActions";
import type { ExportName, ExportSetting } from "./type";

export const hasSuccessfullyPostedSuppressionList = (resultAction: ActionType<typeof sendParsedAsync>): boolean => {
    return isActionOf(sendParsedAsync.success, resultAction);
};

export const getPreparedExportSettings = ({
    settings,
    exportName,
    integration,
    isCrmEnrichmentEnabled = false,
    existingSettings = [],
}: {
    settings: string[];
    exportName: ExportName;
    integration: Integration;
    isCrmEnrichmentEnabled?: boolean;
    existingSettings?: readonly ExportSetting[];
}): ExportSetting[] => {
    const config = getConfigByExportName(integration, exportName);
    const tableData: { field: string }[] = existingSettings.map(({ field }) => ({ field }));

    return settings.map((setting: keyof SimilarwebFieldsConfig<AccountsFieldKey>) => {
        const { fieldName, metadata } = getUniqueFieldName(setting, tableData);
        tableData.push({ field: fieldName });

        const sourceType = getExportSettingSourceType(metadata || setting);
        const defaultCrmField = getDefaultContactsCrmValue(metadata || setting, integration);
        const isEnrichmentOnly = !!config[metadata || setting]?.isEnrichmentOnly;

        const forEnrichment = isEnrichmentOnly
            ? true
            : integration === "hubspot" && isCrmEnrichmentEnabled
              ? isFieldEligibleForEnrichment(sourceType)
              : false;

        return {
            title: getExportSettingTitle(metadata, sourceType, config),
            sourceType,
            externalField: defaultCrmField || "",
            example: config[metadata || setting]?.example || "",
            overwrite: false,
            field: fieldName,
            metadata,
            forExport: !isEnrichmentOnly,
            forEnrichment,
        };
    });
};
